import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { masterStudyApiService } from "modules/Clinical/StudyDetails/api/study.api";
import { CurrentUser, GetUserData } from "common/service/CommonService";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_MODULES, PERMISSION_ACTIONS } from "common/constants/PermissionConstants";

interface ApiResponse<T = any> {
  data: T;
  message: string;
  statuscode: number;
}

// ------------------------------------------------------------------
//-- Study
// ------------------------------------------------------------------

export const useMasterStudyService = ({
  searchText,
  pageNumber,
  pageSize,
  id,
  enabledSelectAll = false,
  enabledDropDown = false,
  enabledSelect = false,
  studyid,
  includedraft = false,
  checklisttype,
  periodnumber,
  tabname,
  enabledPeriodVisibility = false,
  crfmodule,
}: {
  searchText?: string | null;
  pageNumber?: number;
  pageSize?: number;
  id?: number | null;
  enabledSelectAll?: boolean;
  enabledDropDown?: boolean;
  enabledSelect?: boolean;
  studyid?: number | null;
  includedraft?: boolean;
  checklisttype ?:string;
  periodnumber?:string;
  tabname?: string;
  enabledPeriodVisibility?: boolean;
  crfmodule?:string;
} = {}) => {  
  const { hasPermission } = usePermission();
  const hasAllStudiesRight = hasPermission(PERMISSION_MODULES.GLOBAL_STUDIES, PERMISSION_ACTIONS.ALL_STUDIES);

  const SelectAllQuery = useQuery({
    queryKey: ["masterstudy-selectAll", searchText, pageNumber, pageSize,studyid],
    queryFn: () =>
      masterStudyApiService.getSelectAllData(searchText, pageNumber, pageSize,studyid),
    enabled: enabledSelectAll && !!(pageNumber && pageSize)
  });

  const DropDownAllQuery = useQuery({
    queryKey: ["masterstudy-dropdown-selectAll", id, hasAllStudiesRight, includedraft],
    queryFn: async () => {
      const result = await masterStudyApiService.getDropDownData(id!,includedraft);
      if (hasAllStudiesRight) {
        return result;
      }
      const user = GetUserData();
      const hasUserStudies = Array.isArray(user?.studies) && user.studies.length > 0;
      if (hasUserStudies && result && Array.isArray(result.data)) {
        const allowedIds = new Set(
          (user.studies as any[]).map((s: any) => String(s.studyid))
        );
        return {
          ...result,
          data: result.data.filter((study: any) => allowedIds.has(String(study.studyid))),
        };
      }
      return {
        ...result,
        data: [],
      };
    },
    enabled: enabledDropDown 
  });

  const SelectQuery = useQuery({
    queryKey: ["masterstudy-select", id],
    queryFn: () => masterStudyApiService.getSelectData(id!),
    enabled: enabledSelect && !!id,
  });

  const InsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) =>
      masterStudyApiService.insertData(payload),
  });

  const checklistSelectallQuery = useQuery({
    queryKey: ["checklist-selectall", studyid, checklisttype, periodnumber, searchText],
    queryFn: () => masterStudyApiService.getChecklistSelectAllData(studyid, checklisttype, periodnumber, searchText),
    enabled: !!studyid && !!checklisttype && !!periodnumber,
  });

  const periodVisibilityQuery = useQuery({
    queryKey: ["checklist-period-visibility", studyid, tabname],
    queryFn: () => masterStudyApiService.getPeriodVisibilityData(studyid, tabname),
    enabled: enabledPeriodVisibility && !!studyid && !!tabname,
  });

  const ChecklistInsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) =>
      masterStudyApiService.insertChecklistData(payload),
  });

  const ChecklistUpdateMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) =>
      masterStudyApiService.updateChecklistData(payload),
  });

  const subjectCRFSelectallQuery = useQuery({
    queryKey: ["subject-crf-selectall", studyid, periodnumber, searchText, crfmodule],
    queryFn: () => masterStudyApiService.getSubjectCRFSelectAllData(studyid, periodnumber, searchText, crfmodule),
    enabled: !!studyid && !!periodnumber && !!crfmodule,
  });

  const SubjectCRFInsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) =>
      masterStudyApiService.insertSubjectCRFData(payload),
  });

  return {
    SelectAllQuery,
    SelectQuery,
    InsertMutation,
    DropDownAllQuery,
    checklistSelectallQuery,
    periodVisibilityQuery,
    ChecklistInsertMutation,
    ChecklistUpdateMutation,
    subjectCRFSelectallQuery,
    SubjectCRFInsertMutation,
  };
};
